import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as services from "../../store/service";
import { ICSIDUserStatus } from "../../store/csid/types";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

interface IProps {
    closeButtonText: string;
    onCloseCallback: any;
    openAsDialog: boolean;
    submitButtonText: string;
    onSubmitCallback: any;
    displaySSNInputNumber: boolean;
    dobRequired: boolean;
    csidUserStatus: ICSIDUserStatus;
    dispalyAdditionalHeaderMessage?: boolean;
    ignoreCSIDSubmission?: boolean;
}

const FullSINComponent: React.FunctionComponent<IProps> = (props) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();

    const initState = {
        "check-ssn-first-3-digit": "",
        "check-ssn-second-3-digit": "",
        "check-ssn-third-3-digit": "",
        "confirm-ssn-first-3-digit": "",
        "confirm-ssn-second-3-digit": "",
        "confirm-ssn-third-3-digit": "",
        birthDay: "1",
        birthMonth: "1",
        birthYear: new Date().getFullYear().toString(),
        ssnValid: true,
        errorMessage: tLang("dashboard.creditScore.fullSINComp.errorMessage"),
        dobValid: true,
        notOlderMessage: tLang("dashboard.creditScore.fullSINComp.notOlderMessage"),
    };

    const status = {
        success: "success",
        error: "error",
        apiException: "api-exception",
        apiStatus: "api-status",
    };

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const isDate18orMoreYearsOld = (day: number, month: number, year: number) => {
        return new Date(year + 18, month - 1, day).getTime() <= new Date().getTime();
    };

    let strCloseButtonText = tLang("dashboard.creditScore.fullSINComp.closeButtonText");
    let strSubmitButtonText = tLang("dashboard.creditScore.fullSINComp.submitButtonText");
    let blnOpenAsDialog = false;
    let ssnInputFieldType = "password";
    let blnDispalyAdditionalHeaderMessage = false;
    let blnIgnoreCSIDSubmission = false;
    // set default value for props, if not passed
    if (props.closeButtonText != null) {
        strCloseButtonText = props.closeButtonText;
    }
    if (props.submitButtonText != null) {
        strSubmitButtonText = props.submitButtonText;
    }
    if (props.openAsDialog != null) {
        blnOpenAsDialog = props.openAsDialog;
    }
    if (props.displaySSNInputNumber) {
        ssnInputFieldType = "text";
    }
    if (props.dispalyAdditionalHeaderMessage != null) {
        blnDispalyAdditionalHeaderMessage = props.dispalyAdditionalHeaderMessage;
    }
    if (props.ignoreCSIDSubmission != null) {
        blnIgnoreCSIDSubmission = props.ignoreCSIDSubmission;
    }
    // use form-submit-hook
    const { register, errors, reset, formState, handleSubmit, clearErrors } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const { isSubmitting } = formState;
    const [isLoading, setIsLoading] = useState(false);
    const [fullSSN, setFullSSN] = useState(initState);
    // const [fullSSN, setFULLSSN] = useState({ "ssn": "", "dob": "" })

    // in case of dialog, register event for escape press
    if (blnOpenAsDialog) {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }

        });
    }

    function onTextBoxChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        onChangeHandler(event);
        let targetName = event.target.name;
        let targetValue = event.target.value;

        switch (targetName) {
            case "check-ssn-first-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("check-ssn-second-3-digit")?.focus();
                }
                break;
            case "check-ssn-second-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("check-ssn-third-3-digit")?.focus();
                }
                break;
            case "check-ssn-third-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("confirm-ssn-first-3-digit")?.focus();
                }
                break;
            case "confirm-ssn-first-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("confirm-ssn-second-3-digit")?.focus();
                }
                break;
            case "confirm-ssn-second-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("confirm-ssn-third-3-digit")?.focus();
                }
                break;
            default:
                break;
        }
    }

    function onChangeHandler(event: any) {
        clearErrors();
        const elName = event.currentTarget.name;
        setFullSSN({ ...fullSSN, [elName as string]: event.currentTarget.value });
    }

    const invokePostSubmitCallback = (status: string, data: any) => {
        if (props.onSubmitCallback != null) {
            props.onSubmitCallback({ status: status, data: data });
        }
    };

    // handle form submit
    const onSubmit = () => {
        const ssn = fullSSN["check-ssn-first-3-digit"] + fullSSN["check-ssn-second-3-digit"] + fullSSN["check-ssn-third-3-digit"];
        const confirmSSN = fullSSN["confirm-ssn-first-3-digit"] + fullSSN["confirm-ssn-second-3-digit"] + fullSSN["confirm-ssn-third-3-digit"];
        if (ssn === confirmSSN) {
            setFullSSN({ ...fullSSN, ssnValid: true });
            let dob = "";
            if (props.dobRequired) {
                dob = fullSSN.birthMonth + "/" + fullSSN.birthDay + "/" + fullSSN.birthYear;
                const tempDay = parseInt(fullSSN.birthDay, 10);
                const tempMonth = parseInt(fullSSN.birthMonth, 10);
                const tempYear = parseInt(fullSSN.birthYear, 10);
                const is18YearsOld = isDate18orMoreYearsOld(tempDay, tempMonth, tempYear);
                if (!is18YearsOld) {
                    // show error
                    setFullSSN({ ...fullSSN, dobValid: false });
                    return;
                } else {
                    setFullSSN({ ...fullSSN, dobValid: true });
                }
            }
            // check if we should invoke the CSID submission
            if (!blnIgnoreCSIDSubmission) {
                // set the loader into UI;
                setIsLoading(true);

                if (props.csidUserStatus?.csidActivationStatus === "ACTIVATED") {
                    services
                        .updateUserinCSID(ssn, dob)
                        .then((data) => {
                            // remove loader from UI
                            setIsLoading(false);
                            if (data["status-code"] === "200") {
                                invokePostSubmitCallback(status.success, { ssn: ssn, dob: dob, detail: data });
                            } else {
                                invokePostSubmitCallback(status.error, { reason: status.apiStatus, detail: data });
                            }
                        })
                        .catch((error) => {
                            setIsLoading(false);
                            invokePostSubmitCallback(status.error, { reason: status.apiException, detail: error });
                        });
                } else {
                    // send http request to enroll the user in CSID.
                    services
                        .enrollUserinCSID(ssn, dob)
                        .then((data) => {
                            // remove loader from UI
                            setIsLoading(false);
                            if (data["status-code"] === "200") {
                                invokePostSubmitCallback(status.success, { ssn: ssn, dob: dob, detail: data });
                            } else {
                                invokePostSubmitCallback(status.error, { reason: status.apiStatus, detail: data });
                            }
                        })
                        .catch((error) => {
                            setIsLoading(false);
                            invokePostSubmitCallback(status.error, { reason: status.apiException, detail: error });
                        });
                }
            } else {
                // if we need to to ignore the CSID submission,
                // then invoke the success
                invokePostSubmitCallback(status.success, { ssn: ssn, dob: dob, detail: {} });
            }
        } else {
            setFullSSN({ ...fullSSN, ssnValid: false });
        }
    };

    // handle close button click
    // add reason, so that the callback function can identify, if it's cancel (incase of dialog: ESC press) or button click close


    return (
        <React.Fragment>
            <div className={`${isLoading && "loading"}`}>
                <form id="form-fullssn" onSubmit={handleSubmit(onSubmit)}>
                    <div className="dialog-header">
                        {props.openAsDialog && (
                            <div className="dialog-icon status-default">
                                <RenderIcons iconName="user-shield" />
                            </div>
                        )}
                        <h1>{tLang("dashboard.creditScore.fullSINComp.header")}</h1>
                    </div>

                    <div className="dialog-body">
                        <div className="box-description">
                            {blnDispalyAdditionalHeaderMessage && (
                                <React.Fragment>
                                    <h2>{tLang("dashboard.creditScore.fullSINComp.header2")}</h2>
                                    <p>
                                        {tLang("dashboard.creditScore.fullSINComp.additionalHeaderMessage1")}.
                                        <br />
                                        {tLang("dashboard.creditScore.fullSINComp.additionalHeaderMessage2")}
                                    </p>
                                </React.Fragment>
                            )}
                            <p>
                                {tLang("dashboard.creditScore.fullSINComp.headerText1")}
                                {tLang("dashboard.creditScore.fullSINComp.headerText2")}
                            </p>
                        </div>

                        <div className="form-row">
                            <div className="form-input">
                                <div className="row">
                                    <div className="form-label">
                                        <label htmlFor="check-ssn-first-3-digit" className="control-label">
                                            <span className="red-text"> </span>
                                            {tLang("dashboard.creditScore.fullSINComp.enterSIN")}
                                        </label>
                                    </div>
                                    <div className="space-divider margin-top-l"></div>
                                    <div className="form-input row">
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="check-ssn-first-3-digit" id="check-ssn-first-3-digit"
                                                maxLength={3}
                                                className="inputbox"
                                                autoComplete="off"
                                                autoFocus={true}
                                                onChange={onTextBoxChangeHandler}
                                            />
                                        </div>
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="check-ssn-second-3-digit"
                                                id="check-ssn-second-3-digit"
                                                maxLength={3}
                                                autoComplete="off"
                                                className="inputbox"
                                                onChange={onTextBoxChangeHandler}
                                            />
                                        </div>
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="check-ssn-third-3-digit"
                                                id="check-ssn-third-3-digit"
                                                maxLength={3}
                                                autoComplete="off"
                                                className="inputbox"
                                                onChange={onTextBoxChangeHandler}
                                            />
                                        </div>
                                    </div>
                                    {({ ...errors["check-ssn-first-3-digit"] }.type === "required" ||
                                        { ...errors["check-ssn-second-3-digit"] }.type === "required" ||
                                        { ...errors["check-ssn-third-3-digit"] }.type === "required") && <span className="form-error">{tLang("dashboard.creditScore.fullSINComp.requiredErrorMessage")}</span>}

                                    {({ ...errors["check-ssn-first-3-digit"] }.type === "pattern" ||
                                        { ...errors["check-ssn-second-3-digit"] }.type === "pattern" ||
                                        { ...errors["check-ssn-third-3-digit"] }.type === "pattern") && (
                                        <span className="form-error"> {tLang("dashboard.creditScore.fullSINComp.invalidErrorMessage")}</span>
                                    )}
                                </div>
                                <div className="row">
                                    <div className="form-label">
                                        <label className="control-label" htmlFor="confirm-ssn-first-3-digit">
                                            <span className="red-text"> </span>
                                            {tLang("dashboard.creditScore.fullSINComp.confirmSIN")}
                                        </label>
                                    </div>
                                    <div className="space-divider margin-top-l"></div>
                                    <div className="form-input row">
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="confirm-ssn-first-3-digit"
                                                id="confirm-ssn-first-3-digit"
                                                maxLength={3}
                                                className="inputbox"
                                                autoComplete="off"
                                                onChange={onTextBoxChangeHandler}
                                            />
                                        </div>
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="confirm-ssn-second-3-digit"
                                                id="confirm-ssn-second-3-digit"
                                                maxLength={3}
                                                autoComplete="off"
                                                className="inputbox"
                                                onChange={onTextBoxChangeHandler}
                                            />
                                        </div>
                                        <div className="col-xs-4 col-sm-2 col-gutter-horizontal">
                                            <input
                                                ref={register({ required: false, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                type={ssnInputFieldType}
                                                name="confirm-ssn-third-3-digit"
                                                id="confirm-ssn-third-3-digit"
                                                maxLength={3}
                                                autoComplete="off"
                                                className="inputbox"
                                                onChange={onChangeHandler}
                                            />
                                        </div>
                                    </div>
                                    {({ ...errors["confirm-ssn-first-3-digit"] }.type === "required" ||
                                        { ...errors["confirm-ssn-second-3-digit"] }.type === "required" ||
                                        { ...errors["confirm-ssn-third-3-digit"] }.type === "required") && (
                                        <span className="form-error">{tLang("dashboard.creditScore.fullSINComp.confirmSINRequired")}</span>
                                    )}

                                    {({ ...errors["confirm-ssn-first-3-digit"] }.type === "pattern" ||
                                        { ...errors["confirm-ssn-second-3-digit"] }.type === "pattern" ||
                                        { ...errors["confirm-ssn-third-3-digit"] }.type === "pattern") && (
                                        <span className="form-error"> {tLang("dashboard.creditScore.fullSINComp.invalidErrorMessage")}</span>
                                    )}
                                </div>
                                {!fullSSN.ssnValid && <p style={{ color: "red" }}>{fullSSN.errorMessage}</p>}
                            </div>
                        </div>

                        {props.dobRequired && (
                            <div className="form-row">
                                <div className="form-label">
                                    <label htmlFor="dob">
                                        {tLang("dashboard.creditScore.fullSINComp.dateOfBirthText")}{" "}
                                        <span className="required" title="Mandatory">
                                            {" "}
                                            *{" "}
                                        </span>{" "}
                                    </label>
                                </div>
                                <div className="form-input row">
                                    <div className="col-xs-4 col-sm-3 col-no-gutter">
                                        <select
                                            ref={register}
                                            name="birthDay"
                                            className="inputbox"
                                            id="label-dob-day"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthDay}
                                        >
                                            {Array(31)
                                                .fill(null)
                                                .map((value, index) => {
                                                    return (
                                                        <option key={index + 1} value={index + 1}>
                                                            {index + 1}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                    <div className="col-xs-4 col-sm-3 col-gutter-horizontal">
                                        <select
                                            ref={register}
                                            name="birthMonth"
                                            className="inputbox"
                                            id="label-dob-month"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthMonth}
                                        >
                                            {months.map((month, index) => {
                                                return (
                                                    <option key={month} value={index + 1}>
                                                        {month}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-xs-4 col-sm-3 col-no-gutter">
                                        <select
                                            ref={register}
                                            name="birthYear"
                                            className="inputbox"
                                            id="label-dob-year"
                                            onChange={onChangeHandler}
                                            value={fullSSN.birthYear}
                                        >
                                            {Array(115)
                                                .fill(null)
                                                .map((value, index) => {
                                                    const optionValue = new Date().getFullYear() - index;
                                                    return (
                                                        <option key={optionValue} value={optionValue}>
                                                            {optionValue}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                                {!fullSSN.dobValid && <p style={{ color: "red" }}>{fullSSN.notOlderMessage}</p>}
                            </div>
                        )}
                    </div>

                    <div className="dialog-footer">
                        <div className="row between-xs">
                            <div className="col-xs col-no-gutter flex-grow-null">
                            </div>
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <button type="submit" className="btn-default btn-primary" disabled={Object.keys(errors).length > 0 || isSubmitting}>
                                    <span className="btn-text">{strSubmitButtonText}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </React.Fragment>
    );
};

export default FullSINComponent;
